// MARK://dhk8/admin-shell/lib/clients/reservations
// Admin client for dhk8-pos-floor reservations (Fase 1 demo Toyo).
//
// F9.17 / ADR-0008: lecturas Y escrituras por el grafo (pos-floor). El JWT lleva
// el tenant; cada subgraph lo impone vía RLS, así que el tenantId NO va en los
// inputs de las mutations.

import { gqlClient } from '../gql.js'
import {
  AdminReservationsQuery,
  AdminCreateReservationMutation,
  AdminUpdateReservationMutation,
  AdminCancelReservationMutation,
} from '../queries/reservations.queries.js'

/** Lane #163. */
export type ReservationOccasion = 'birthday' | 'anniversary' | 'date' | 'business' | 'other'
export type ReservationSource = 'walk_in' | 'phone' | 'web' | 'instagram' | 'recommended' | 'other'

export interface ReservationDto {
  id: string
  tenantId: string
  branchId: string | null
  tableId: string | null
  name: string
  phone: string | null
  email: string | null
  partySize: number
  reservedAt: string
  durationMin: number | null
  status: 'CONFIRMED' | 'ARRIVED' | 'SEATED' | 'CANCELLED' | 'NO_SHOW'
  notes: string | null
  allergies: string | null
  highChair: boolean
  wheelchair: boolean
  occasion: ReservationOccasion | null
  source: ReservationSource | null
  confirmedAt: string | null
  cancelledAt: string | null
  seatedSessionId: string | null
  reservationCode?: string | null
  reservationDay?: string | null
  createdAt: string
}

export interface CreateReservationInput {
  name: string
  phone?: string
  email?: string
  partySize: number
  reservedAt: string
  notes?: string
  durationMin?: number
  branchId?: string
  allergies?: string
  highChair?: boolean
  wheelchair?: boolean
  occasion?: ReservationOccasion
  source?: ReservationSource
}

export async function listReservations(opts: { date?: string; from?: string; to?: string } = {}, signal?: AbortSignal): Promise<ReservationDto[]> {
  // F9.5 / ADR-0009: lectura por el grafo (pos-floor, tenant del JWT, RLS). Mismo
  // campo `reservations` que ya consume el POS. Escrituras siguen REST.
  const ctx = signal ? { fetchOptions: { signal } } : undefined
  const res = await gqlClient.query(
    AdminReservationsQuery,
    { branchId: null, date: opts.date ?? null, from: opts.from ?? null, to: opts.to ?? null, status: null },
    ctx,
  ).toPromise()
  if (res.error) throw res.error
  return (res.data?.reservations ?? []) as unknown as ReservationDto[]
}

export async function createReservation(input: CreateReservationInput): Promise<ReservationDto> {
  // F9.17 / ADR-0008: escritura por el grafo. tenantId lo pone el JWT (no va en input).
  const gqlInput: {
    name: string; partySize: number; reservedAt: string
    phone?: string; email?: string; notes?: string; durationMin?: number; branchId?: string
    allergies?: string; highChair?: boolean; wheelchair?: boolean
    occasion?: ReservationOccasion; source?: ReservationSource
  } = { name: input.name, partySize: input.partySize, reservedAt: input.reservedAt }
  if (input.phone) gqlInput.phone = input.phone
  if (input.email) gqlInput.email = input.email
  if (input.notes) gqlInput.notes = input.notes
  if (input.durationMin !== undefined) gqlInput.durationMin = input.durationMin
  if (input.branchId) gqlInput.branchId = input.branchId
  // Lane #163 — campos extra
  if (input.allergies) gqlInput.allergies = input.allergies
  if (input.highChair !== undefined) gqlInput.highChair = input.highChair
  if (input.wheelchair !== undefined) gqlInput.wheelchair = input.wheelchair
  if (input.occasion) gqlInput.occasion = input.occasion
  if (input.source) gqlInput.source = input.source
  const res = await gqlClient.mutation(AdminCreateReservationMutation, { input: gqlInput }).toPromise()
  if (res.error) throw res.error
  const r = res.data?.createReservation
  if (!r) throw new Error('No se pudo crear la reserva')
  return r as unknown as ReservationDto
}

export async function updateReservation(
  id: string,
  input: Partial<Pick<ReservationDto,
    'name' | 'phone' | 'email' | 'partySize' | 'reservedAt' | 'durationMin' |
    'notes' | 'status' | 'tableId' | 'allergies' | 'highChair' | 'wheelchair' |
    'occasion' | 'source'
  >>,
): Promise<ReservationDto> {
  // F9.17 / ADR-0008: escritura por el grafo (tenant del JWT, IDOR-guard por RLS).
  const gqlInput: {
    name?: string; phone?: string | null; email?: string | null; partySize?: number
    reservedAt?: string; durationMin?: number | null; notes?: string | null; status?: string
    tableId?: string | null; allergies?: string | null; highChair?: boolean; wheelchair?: boolean
    occasion?: ReservationOccasion | null; source?: ReservationSource | null
  } = {}
  if (input.name !== undefined) gqlInput.name = input.name
  if (input.phone !== undefined) gqlInput.phone = input.phone
  if (input.email !== undefined) gqlInput.email = input.email
  if (input.partySize !== undefined) gqlInput.partySize = input.partySize
  if (input.reservedAt !== undefined) gqlInput.reservedAt = input.reservedAt
  if (input.durationMin !== undefined) gqlInput.durationMin = input.durationMin
  if (input.notes !== undefined) gqlInput.notes = input.notes
  if (input.status !== undefined) gqlInput.status = input.status
  if (input.tableId !== undefined) gqlInput.tableId = input.tableId
  if (input.allergies !== undefined) gqlInput.allergies = input.allergies
  if (input.highChair !== undefined) gqlInput.highChair = input.highChair
  if (input.wheelchair !== undefined) gqlInput.wheelchair = input.wheelchair
  if (input.occasion !== undefined) gqlInput.occasion = input.occasion
  if (input.source !== undefined) gqlInput.source = input.source
  const res = await gqlClient.mutation(AdminUpdateReservationMutation, { id, input: gqlInput }).toPromise()
  if (res.error) throw res.error
  const r = res.data?.updateReservation
  if (!r) throw new Error('No se pudo actualizar la reserva')
  return r as unknown as ReservationDto
}

export async function cancelReservation(id: string): Promise<ReservationDto> {
  // F9.17 / ADR-0008: cancelación por el grafo (tenant del JWT, IDOR-guard por RLS).
  const res = await gqlClient.mutation(AdminCancelReservationMutation, { id }).toPromise()
  if (res.error) throw res.error
  const r = res.data?.cancelReservation
  if (!r) throw new Error('No se pudo cancelar la reserva')
  return r as unknown as ReservationDto
}
